import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import SwitchHorizontalIcon from '@/assets/icons/switch-horizontal.svg';
import UserIcon from '@/assets/icons/user.svg';
import { Input } from '@/components/ui/input';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { getTargetCpa } from '@/utils/targets';

export function Cpa() {
  const intl = useIntl();
  const {
    hookForm,
    hiringTarget: { targetBudget: budget },
    isFetching,
    setActiveEditField,
    isTargetMetricsReadOnly,
  } = useEditCampaignContext();

  const {
    register,
    setValue,
    formState: { errors },
    control,
  } = hookForm;

  // Update target CPA when target applicants changes
  const handleTargetApplicantsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const targetApplicants =
      e.target.value === '' ? null : Number(e.target.value);

    setValue('target_applicants', targetApplicants, {
      shouldDirty: true,
    });

    setValue('expected_cpa', getTargetCpa(budget, targetApplicants), {
      shouldDirty: true,
    });
  };

  return (
    <div className="flex items-start gap-1">
      <Input
        isNumber
        label={intl.formatMessage({
          defaultMessage: 'Target cost per applicant',
          description:
            'Edit campaign: dollar CPA goal (may be derived from applicants)',
        })}
        {...register('expected_cpa', {
          setValueAs: value => (value === '' ? null : Number(value)),
        })}
        placeholderLabel="$"
        placeholder={intl.formatMessage({
          defaultMessage: 'Enter target CPA',
          description: 'Placeholder for dollar CPA on edit campaign form',
        })}
        error={errors.expected_cpa?.message}
        isFetching={isFetching}
        onFocus={() => setActiveEditField('expected_cpa')}
        onBlur={() => setActiveEditField(null)}
        readOnly={isTargetMetricsReadOnly}
      />

      <img src={SwitchHorizontalIcon} alt="Switch" className="mt-7" />

      <Controller
        control={control}
        name="target_applicants"
        render={({ field }) => (
          <Input
            {...field}
            value={field.value ?? ''}
            isNumber
            label={intl.formatMessage({
              defaultMessage: 'Target applicants',
              description:
                'Edit campaign: desired applicant volume for the goal',
            })}
            onChange={e => {
              field.onChange(e);
              handleTargetApplicantsChange(e);
            }}
            placeholderIcon={UserIcon}
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter target applicants',
              description: 'Placeholder for applicant count linked to CPA',
            })}
            error={errors.target_applicants?.message}
            isFetching={isFetching}
            onFocus={() => setActiveEditField('target_applicants')}
            onBlur={() => setActiveEditField(null)}
            readOnly={isTargetMetricsReadOnly}
          />
        )}
      />
    </div>
  );
}
